import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import {
  ArrowLeftOnRectangleIcon,
  ArrowPathRoundedSquareIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  Bars3Icon,
  Cog6ToothIcon,
  Cog8ToothIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  RocketLaunchIcon,
  SparklesIcon,
  SquaresPlusIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import * as Avatar from '@radix-ui/react-avatar';
import clsx from 'clsx';
import { differenceInDays, endOfDay } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { Link as RouterLink, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { usePanel } from '@dashboard/context/PanelContext';
import useMatchMedia from '@dashboard/hooks/useMatchMedia';
import usePrevious from '@dashboard/hooks/usePrevious';
import { useUpdateNotification } from '@dashboard/hooks/useUpdateNotification';
import getSafeReadableColor from '@dashboard/scenes/PageEdit/utils/getSafeReadableColor';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import Button from '@dashboard/components/Button';
import Logo from '@dashboard/components/Logo';

const StyledNav = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  padding-top: ${p => (p.hasBanner ? '48px' : 0)};
  top: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
  width: 260px;
  transition: left 200ms ease;
  z-index: 40;

  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(#fff, 0.5);
    border: 3px solid transparent;
    border-radius: 9px;
    background-clip: content-box;
  }

  @media (max-width: 768px) {
    padding-top: 32px;
    ${p => (p.isOpen ? `left: 0px; box-shadow: var(--shadow);` : `left: -250px`)};
  }
`;

function NavList({ children }) {
  return <ul className="mb-4 px-4 mt-0 list-style-none">{children}</ul>;
}

function NavLinkText({ children, className }) {
  return <span className={clsx(`align-middle font-medium text-lg`, className)}>{children}</span>;
}

const NavLink = React.forwardRef(function NavLink(
  { children, className = '', isActive, ...props },
  ref,
) {
  return (
    <li>
      <RouterNavLink
        ref={ref}
        exact={true}
        className={clsx(
          `group flex w-full items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
          className,
          {
            'text-chocolate-600 bg-gray-50': isActive(),
            'text-gray-900 bg-transparent': !isActive(),
          },
        )}
        {...props}
      >
        {children}
      </RouterNavLink>
    </li>
  );
});

const PopoverLink = React.forwardRef(function PopoverLink(
  { children, className = '', ...props },
  ref,
) {
  return (
    <RouterLink
      ref={ref}
      className={clsx(
        `group flex w-full items-center space-x-2.5 rounded-md px-[18px] transition-all duration-300 hover:text-chocolate-600 py-1.5 text-gray-900 cursor-pointer`,
        className,
      )}
      {...props}
    >
      {children}
    </RouterLink>
  );
});

const PopoverAnchor = React.forwardRef(function PopoverAnchor(
  { children, className = '', ...props },
  ref,
) {
  return (
    <a
      ref={ref}
      target="_blank"
      className={clsx(
        `group flex w-full items-center space-x-2.5 rounded-md px-[18px] transition-all duration-300 hover:text-chocolate-600 py-1.5 text-gray-900`,
        className,
      )}
      {...props}
    >
      {children}
    </a>
  );
});

function Navigation() {
  const isSmall = useMatchMedia('(max-width: 768px)');
  const [panelState, panelDispatch] = usePanel();
  const navNode = useRef(null);
  const menuButton = useRef(null);
  const { pathname } = useLocation();
  const previousPathname = usePrevious(pathname);
  const {
    user,
    seller,
    isAdminLoggedInAsSeller,
    isTrialing,
    isConnectedToStripe,
    hasActiveSubscription,
    isAuthenticating,
    isCanceled,
    isTrialExpired,
    plan,
    hasPastDueSubscription,
  } = useAuth();
  const { hasNewUpdates, markUpdatesAsSeen } = useUpdateNotification();
  const NavRef = useRef(null);
  //const [ordersOpen, setOrdersOpen] = useState(false);

  const isOpen = panelState.nav;
  const setIsOpen = value => panelDispatch({ type: 'set', panel: 'nav', value });

  // handle click outside NavRef
  useEffect(() => {
    function onClick(e) {
      if (
        NavRef &&
        NavRef.current &&
        menuButton &&
        menuButton.current &&
        !menuButton.current.contains(e.target)
      ) {
        if (!NavRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      }
    }

    window.addEventListener('click', onClick);

    return () => window.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    if (isSmall && previousPathname && previousPathname && pathname !== previousPathname) {
      setIsOpen(false);
    }
  }, [previousPathname, pathname]);

  useEffect(() => {
    function clickOutside(event) {
      if (
        isSmall &&
        navNode.current &&
        !navNode.current.contains(event.target) &&
        menuButton.current &&
        !menuButton.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    window.addEventListener('click', clickOutside, true);

    return () => window.removeEventListener('click', clickOutside, true);
  }, []);

  const trialDaysRemaining =
    seller.trialEnd && differenceInDays(endOfDay(new Date(seller.trialEnd)), new Date());

  // hide navigation if customer has no active subscription
  const shouldShowNavigation = !(
    (isAuthenticating === false &&
      hasActiveSubscription === false &&
      !isTrialing &&
      !isAdminLoggedInAsSeller) ||
    hasPastDueSubscription
  );

  const isPageDetail =
    pathname.includes('/pages/') &&
    !pathname.includes('/checkouts') &&
    !pathname.includes('/forms');

  const isUpgradePage = pathname.includes('/upgrade') || pathname.includes('/payment-due');

  return (
    <>
      {isSmall && (
        <div className="block md:hidden absolute top-6 left-4 z-50">
          <Button
            appearance="light"
            onFocus={event => event.stopPropagation()}
            onClick={() => setIsOpen(!isOpen)}
            ref={menuButton}
          >
            <Bars3Icon className="w-5 h-5" />

            <div className="sr-only">Toggle Nav</div>
          </Button>
        </div>
      )}

      <StyledNav
        hasBanner={!isConnectedToStripe && !isPageDetail && user.onboardingStepsClosed}
        className={clsx('bg-white', {
          'border-r border-gray-100': isOpen || !isSmall,
        })}
        isOpen={isOpen}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        innerRef={NavRef}
      >
        <div>
          <header className="flex flex-1 justify-between pt-6 pb-8">
            <nav style={{ width: '100%' }}>
              {!isSmall && (
                <div className="pb-8 pl-4">
                  <RouterLink to="/" className="hover:text-chocolate-600 transition-colors">
                    <Logo size={40} />
                  </RouterLink>
                </div>
              )}

              <NavList>
                {shouldShowNavigation && (
                  <>
                    <NavLink to="/" isActive={() => pathname === '/'}>
                      <HomeIcon className="h-5 w-5" />
                      <NavLinkText>Dashboard</NavLinkText>
                    </NavLink>

                    <NavLink to="/pages" isActive={() => pathname.startsWith('/pages')}>
                      <SquaresPlusIcon className="h-5 w-5" />
                      <NavLinkText>Pages</NavLinkText>
                    </NavLink>

                    <NavLink
                      to="/payments/one-time"
                      isActive={() => pathname.startsWith('/payments/one-time')}
                    >
                      <BanknotesIcon className="h-5 w-5" />
                      <NavLinkText>Payments</NavLinkText>
                    </NavLink>

                    <NavLink
                      to="/payments/subscriptions"
                      isActive={() => pathname.startsWith('/payments/subscriptions')}
                    >
                      <ArrowPathRoundedSquareIcon className="h-5 w-5" />
                      <NavLinkText>Subscriptions</NavLinkText>
                    </NavLink>

                    <NavLink to="/submissions" isActive={() => pathname.startsWith('/submissions')}>
                      <DocumentTextIcon className="h-5 w-5" />
                      <NavLinkText>Submissions</NavLinkText>
                    </NavLink>

                    <NavLink to="/customers" isActive={() => pathname.startsWith('/customers')}>
                      <UserGroupIcon className="h-5 w-5" />
                      <NavLinkText>Customers</NavLinkText>
                    </NavLink>

                    <NavLink to="/coupons" isActive={() => pathname.startsWith('/coupons')}>
                      <ArrowTrendingUpIcon className="h-5 w-5" />
                      <NavLinkText>Coupons</NavLinkText>
                    </NavLink>
                  </>
                )}

                <NavLink to="/settings/general" isActive={() => pathname.startsWith('/settings')}>
                  <Cog6ToothIcon className="h-5 w-5" />
                  <NavLinkText>Store settings</NavLinkText>
                </NavLink>
              </NavList>
            </nav>
          </header>
        </div>

        <div className="my-4 mt-auto">
          {!isUpgradePage && isTrialing && (
            <div className="m-4 border border-gray-100 rounded-md p-3">
              <h3>Grow</h3>
              <p>Free trial ends in {trialDaysRemaining} days</p>
              <Button
                href="/upgrade?upgradeFlow=sidebar"
                appearance="primary"
                className="mt-2 w-full justify-center"
              >
                <BoltIcon className="w-4 h-4 mr-1" /> Upgrade now
              </Button>
            </div>
          )}

          {!isUpgradePage && seller._id && !hasActiveSubscription && !isTrialing && (
            <div className="m-4 border border-gray-100 rounded-md p-3">
              <h3>{plan.title}</h3>
              {isCanceled && <p>Your subscription ended</p>}
              {isTrialExpired && <p>Your trial expired</p>}
              <Button href="/upgrade" appearance="primary" className="mt-2 w-full justify-center">
                <BoltIcon className="w-4 h-4 mr-1" /> Upgrade now
              </Button>
            </div>
          )}

          {(hasNewUpdates || (seller?._id && !hasActiveSubscription)) && (
            <NavList>
              <NavLink
                to="/updates"
                onClick={markUpdatesAsSeen}
                className="-mx-2"
                isActive={() => false}
              >
                <SparklesIcon className="h-5 w-5" />

                <NavLinkText className="!text-base">What&apos;s new</NavLinkText>
                <div className="absolute right-4 bg-chocolate-1000 rounded-full w-2 h-2" />
              </NavLink>
            </NavList>
          )}

          <Profile />
        </div>
      </StyledNav>
    </>
  );
}

function Profile() {
  const { seller, logout, isAdmin, isAuthenticating } = useAuth();
  const { markUpdatesAsSeen } = useUpdateNotification();

  const width = 28;
  const height = 28;

  const logo = getImageSrcAndSrcSet({
    url: seller.logo?.url,
    width,
    height,
  });

  return (
    <Popover as="div" className="relative mx-4 flex-shrink-0">
      {() => (
        <>
          <>
            {!isAuthenticating && (
              <div>
                <PopoverButton className="flex rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-chocolate-500 focus:ring-offset-2 items-center gap-x-3 w-full hover:bg-gray-50 py-2 px-2">
                  <Avatar.Root className="h-7 w-7 flex-shrink-0">
                    {logo ? (
                      <Avatar.Image
                        src={logo.src}
                        srcSet={logo.srcSet}
                        alt={seller.name}
                        className="rounded-full"
                        width={width}
                        height={height}
                      />
                    ) : (
                      <Avatar.Fallback
                        className="w-full h-full flex rounded-full uppercase items-center justify-center overflow-hidden font-bold leading-loose"
                        delayMs={600}
                        style={{
                          background: seller.brandColor,
                          color: getSafeReadableColor(seller.brandColor),
                        }}
                      >
                        {seller.displayName?.charAt(0)}
                      </Avatar.Fallback>
                    )}
                  </Avatar.Root>

                  <div className="text-gray-900 flex-shrink font-medium overflow-hidden whitespace-nowrap w-full text-left">
                    {seller.displayName || seller.name || 'Untitled'}
                  </div>

                  <div className="">
                    <EllipsisHorizontalIcon className="w-5 h-5" />
                  </div>
                </PopoverButton>
              </div>
            )}
          </>

          <Transition>
            <div className="relative">
              <PopoverPanel
                className={clsx(
                  'absolute bottom-14 z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-gray-100 focus:outline-none py-1 text-gray-900',
                  'transition ease-out duration-100',
                  'data-[closed]:opacity-0 data-[closed]:scale-95',
                  'data-[open]:opacity-100 data-[open]:scale-100',
                )}
              >
                <PopoverButton as={PopoverAnchor} href="/help">
                  <MagnifyingGlassIcon className="h-5 w-5" />
                  <span>Help center</span>
                </PopoverButton>

                <PopoverButton as={PopoverLink} to="/account/profile">
                  <UserGroupIcon className="h-5 w-5" />
                  <span>Account</span>
                </PopoverButton>

                <PopoverButton as={PopoverLink} to="/billing/plan">
                  <CreditCardIcon className="h-5 w-5" />
                  <span>Plan & billing</span>
                </PopoverButton>

                <PopoverButton as={PopoverLink} to="/updates" onClick={markUpdatesAsSeen}>
                  <SparklesIcon className="h-5 w-5" />
                  <span>What&apos;s new</span>
                </PopoverButton>

                <PopoverButton as={PopoverAnchor} href="/affiliate-program">
                  <RocketLaunchIcon className="h-5 w-5" />
                  <span>Affiliate program</span>
                </PopoverButton>

                {isAdmin && (
                  <PopoverButton as={PopoverLink} to="/admin">
                    <Cog8ToothIcon className="h-5 w-5" />
                    <span>Admin</span>
                  </PopoverButton>
                )}

                <PopoverButton onClick={logout} as={PopoverLink}>
                  <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                  <span>Sign out</span>
                </PopoverButton>
              </PopoverPanel>
            </div>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default Navigation;
